import classNames from "classnames"
import React from "react"

import { Color } from "../../../../../constants/V2/color"
import BulletItem from "../../../../elements/V2/BulletItem"
import PillButton from "../../../../elements/V2/Buttons/PillButton"
import TextButton from "../../../../elements/V2/Buttons/TextButton"
import GatsbyStoryblokImage from "../../../../elements/V2/GatsbyStoryblokImage"
import GoogleRating from "../../../../elements/V2/GoogleRating"
import ArrowIcon from "../../../../elements/V2/Icons/ArrowIcon"
import Typography from "../../../../elements/V2/Typography"
import LayoutItem from "../../../Layouts/LayoutItem"

import { useScrollAnimateSelf } from "@hooks/V2/useScrollAnimate"
import { getAccentColor, getBackgroundColorClass } from "@utils/V2/color"

type MasterInfoProps = {
  backgroundColor: Color
  headerText: string
  superscriptText?: string
  subscriptText?: string
  bodyText?: string
  featureIconBackgroundColor?: Color
  featureIconUrl?: string
  featureIconAlt?: string
  featureBody?: string
  bulletItems?: React.ComponentProps<typeof BulletItem>[]
  rating?: number
  linkText?: string
  linkUrl?: string
  linkColor?: Color
  buttonStyle?: string
  size?: "small" | "large"
  mobileGridOrder?: number
}

const MasterInfo = ({
  backgroundColor,
  superscriptText,
  subscriptText,
  headerText,
  bodyText,
  featureIconUrl,
  featureIconAlt,
  featureBody,
  bulletItems,
  rating,
  linkText,
  linkUrl,
  linkColor,
  buttonStyle = "text",
  size = "large",
  mobileGridOrder,
  ...props
}: MasterInfoProps) => {
  const { animationRefCallback } = useScrollAnimateSelf(true)
  return (
    <LayoutItem
      mobileGridOrder={mobileGridOrder}
      as="div"
      ref={animationRefCallback}
      className={classNames(
        getBackgroundColorClass(backgroundColor),
        "rounded-lg col-span-4 flex flex-col justify-between gap-20 px-20 py-24 md:gap-24 md:px-24 md:py-32 lg:gap-32 lg:px-32 lg:py-48",
        { "sm:col-span-2 md:col-span-3 lg:col-span-4": size === "small" },
        { "sm:col-span-2 md:col-span-3 lg:col-span-6": size === "large" }
      )}
      {...props}
    >
      <div className="flex flex-col justify-between gap-20 md:gap-24 lg:gap-32">
        <div className="flex flex-col gap-16 lg:gap-20">
          {superscriptText && (
            <Typography
              font="grotesk"
              weight="book"
              size="subscript-lg"
              text={superscriptText}
              color={getAccentColor(backgroundColor)}
              className="uppercase"
            />
          )}
          <Typography
            font="grotesk"
            weight="book"
            size="h3"
            text={headerText}
            color={getAccentColor(backgroundColor)}
            element="h3"
          />
          {subscriptText && (
            <Typography
              font="grotesk"
              weight="book"
              size="subscript-lg"
              text={subscriptText}
              color={getAccentColor(backgroundColor)}
              className="uppercase"
            />
          )}
        </div>
        {bodyText && (
          <Typography
            font="grotesk"
            weight="book"
            size="body-lg"
            text={bodyText}
            color={getAccentColor(backgroundColor)}
          />
        )}
        {featureIconUrl && featureIconAlt && featureBody && (
          <div className="flex gap-20 lg:gap-24">
            <GatsbyStoryblokImage
              image={featureIconUrl}
              alt={featureIconAlt || "Featured icon"}
              aspectRatio="fullWidth"
              className="w-[42px] self-start md:w-[46px] lg:w-[50px]"
            />

            <Typography
              font="grotesk"
              weight="book"
              size="body-md"
              text={featureBody}
              color={getAccentColor(backgroundColor)}
            />
          </div>
        )}
        {bulletItems && (
          <div className="flex flex-col gap-[3px] md:gap-[6px] lg:gap-[9px]">
            {bulletItems.map(({ text }, index) => (
              <BulletItem
                key={`${text.replace(" ", "-")}-${index}`}
                text={text}
                size="body-lg"
                textColor={getAccentColor(backgroundColor)}
                iconOverrideColor={getAccentColor(backgroundColor)}
              />
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-col justify-between gap-20 md:gap-24 lg:gap-32">
        {rating !== undefined && (
          <GoogleRating
            ratingColor={getAccentColor(backgroundColor)}
            rating={rating}
          />
        )}
        {buttonStyle === "text" && linkText && linkUrl && linkColor && (
          <TextButton
            style="icon"
            text={linkText}
            icon={<ArrowIcon />}
            linkUrl={linkUrl}
            color={linkColor}
          />
        )}
        {buttonStyle === "pill" && linkText && linkUrl && linkColor && (
          <PillButton
            text={linkText}
            color={linkColor}
            overrideTextColor={getAccentColor(backgroundColor)}
            linkUrl={linkUrl}
            style="bordered"
            size="large"
          />
        )}
      </div>
    </LayoutItem>
  )
}

export default MasterInfo
